import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { QuoteEvent, QuoteView } from "@contracts/index";
import { ErrorBox } from "../../components/AdminLayout";
import { QuoteDocument } from "../../components/QuoteDocument";
import { api, errorMessage } from "../../lib/api";
import { EVENT_LABEL, formatDate, formatDateTime, formatMoney, statusLabel } from "../../lib/format";
import { QuoteEditor } from "./QuoteEditor";

/** Shows a quote: the editor while it is a draft, a read-only summary afterwards. Always shows actions and events. */
export function QuotePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [events, setEvents] = useState<QuoteEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [q, ev] = await Promise.all([api.admin.getQuote(id), api.admin.quoteEvents(id)]);
      setQuote(q);
      setEvents(ev);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    setQuote(null);
    void load();
  }, [load]);

  async function run(action: () => Promise<QuoteView>, message: string, goTo?: (q: QuoteView) => string) {
    setError(null);
    try {
      const result = await action();
      if (goTo) navigate(goTo(result));
      else {
        setNotice(message);
        await load();
      }
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  if (!quote) return error ? <ErrorBox message={error} /> : <p className="muted">Cargando…</p>;

  const canCancel = quote.status !== "accepted" && quote.status !== "cancelled";
  const canResend = (quote.status === "sent" || quote.status === "viewed") && !quote.expired;
  const recipients = quote.contacts.map((c) => c.email).join(", ");

  return (
    <>
      <section className="card">
        <div className="row">
          <div className="grow">
            <h1>
              {quote.folio} · {quote.title}
            </h1>
            <p className="muted">
              {quote.client.name} · <span className={`badge ${quote.expired ? "expired" : quote.status}`}>
                {statusLabel(quote.status, quote.expired)}
              </span>{" "}
              · vigente hasta {formatDate(quote.validUntil)} ·{" "}
              {formatMoney(quote.totals.totalCents, quote.currency, quote.language)}
            </p>
          </div>
          <a className="button secondary" href={api.admin.quotePdfUrl(quote.id)} target="_blank" rel="noreferrer">
            Ver PDF
          </a>
          {quote.status === "draft" && (
            <button
              className="primary"
              onClick={() => {
                if (confirm(`Se enviará ${quote.folio} a: ${recipients}. Después ya no podrás editarla. ¿Continuar?`)) {
                  void run(() => api.admin.sendQuote(quote.id), `Cotización enviada a ${recipients}.`);
                }
              }}
            >
              Enviar al cliente
            </button>
          )}
          {canResend && (
            <button
              className="secondary"
              onClick={() => {
                if (confirm(`¿Reenviar el enlace de ${quote.folio} a: ${recipients}?`)) {
                  void run(() => api.admin.resendQuote(quote.id), `Enlace reenviado a ${recipients}.`);
                }
              }}
            >
              Reenviar enlace
            </button>
          )}
          <button className="secondary" onClick={() => run(() => api.admin.duplicateQuote(quote.id), "", (q) => `/admin/quotes/${q.id}`)}>
            Duplicar
          </button>
          {canCancel && (
            <button
              className="danger"
              onClick={() => {
                if (confirm(`¿Cancelar la cotización ${quote.folio}?`)) {
                  void run(() => api.admin.cancelQuote(quote.id), "Cotización cancelada.");
                }
              }}
            >
              Cancelar
            </button>
          )}
        </div>
        {notice && <p className="notice">{notice}</p>}
        <ErrorBox message={error} />
      </section>

      {quote.status === "draft" ? (
        <QuoteEditor
          key={quote.updatedAt}
          quote={quote}
          onSaved={(saved) => {
            setNotice("Cambios guardados.");
            setQuote(saved);
            void load();
          }}
        />
      ) : (
        <QuoteSummary quote={quote} />
      )}

      <section className="card">
        <h2>Historial</h2>
        <ul className="events">
          {events.map((e) => (
            <li key={e.id}>
              <span className="muted">{formatDateTime(e.at)}</span> — {EVENT_LABEL[e.type] ?? e.type}
              {e.email ? ` · ${e.email}` : ""}
              {e.details?.fromFolio ? ` (${e.details.fromFolio})` : ""}
              {e.details?.resend ? " (reenvío)" : ""}
              {e.details?.orderName ? ` ${e.details.orderName}` : ""}
              {e.ip ? ` · IP ${e.ip}` : ""}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function QuoteSummary({ quote }: { quote: QuoteView }) {
  return (
    <>
      <section className="card">
        <h2>Contactos con acceso</h2>
        <ul>
          {quote.contacts.map((c) => (
            <li key={c.email}>
              {c.name} · {c.email}
              {c.role ? ` · ${c.role}` : ""}
            </li>
          ))}
        </ul>
        {quote.acceptedBy && (
          <p className="notice">
            Aceptada por {quote.acceptedBy.name} ({quote.acceptedBy.position}, {quote.acceptedBy.email})
            {quote.acceptedAt ? ` el ${formatDateTime(quote.acceptedAt)}` : ""}.
          </p>
        )}
        {quote.shopifyDraftOrderId && (
          <p>
            Pedido en Shopify:{" "}
            <a
              href={`https://admin.shopify.com/store/cooltesters/draft_orders/${quote.shopifyDraftOrderId.split("/").pop()}`}
              target="_blank"
              rel="noreferrer"
            >
              abrir en Shopify
            </a>
            {quote.paymentUrl && (
              <>
                {" · "}
                <a href={quote.paymentUrl} target="_blank" rel="noreferrer">
                  enlace de pago del cliente
                </a>
              </>
            )}
          </p>
        )}
      </section>
      <section className="card">
        <h2 className="muted">Así la ve el cliente</h2>
        <QuoteDocument quote={quote} />
      </section>
    </>
  );
}
